import { Alert, Button, Descriptions, Modal, Table, Typography } from "antd";
import moment from "moment";
import { useTranslation } from "react-i18next";

import { DISPLAY_DATE_FORMAT } from "~/constants";
import { formatConflictMessage } from "./conflicts";
import { ExportPosition, ServiceTrackerExportResult } from "./types";

const MODAL_WIDTH = 800;

const formatDisplayDate = (date: string): string =>
  moment(date).format(DISPLAY_DATE_FORMAT);

/* The app has an English UI too, so the operator's language decides. */
export const formatEuro = (value: number, locale: string): string =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
  }).format(value);

const PositionTable = ({
  title,
  positions,
  withReason,
}: {
  title: string;
  positions: ExportPosition[];
  withReason?: boolean;
}) => {
  const { t } = useTranslation("ServiceTrackerExport");

  if (!positions.length) {
    return null;
  }

  return (
    <Table<ExportPosition>
      size="small"
      pagination={false}
      style={{ marginTop: 8 }}
      title={() => <strong>{title}</strong>}
      rowKey={(row, index) => `${row.customerNumber}-${index}`}
      dataSource={positions}
      columns={[
        {
          title: t("modal.colCustomer"),
          dataIndex: "customerNumber",
          width: 90,
        },
        {
          title: t("modal.colAmount"),
          dataIndex: "grossAmount",
          width: 100,
          align: "right",
          render: (value: string) => `${value} €`,
        },
        ...(withReason
          ? [
              {
                title: t("modal.colReason"),
                dataIndex: "reason",
                width: 190,
              },
            ]
          : []),
        { title: t("modal.colTitle"), dataIndex: "title", ellipsis: true },
      ]}
    />
  );
};

interface PreviewModalProps {
  open: boolean;
  exporting: boolean;
  dateFrom: string;
  dateTo: string;
  result: ServiceTrackerExportResult;
  onExport: () => void;
  onClose: () => void;
}

/*
 * The preview is NOT the safety net — a person plausibility-checks the generated
 * CSV before it is imported into EasyBill, and that check is what stands between
 * this and a customer invoice. So this modal reports, it does not certify: the
 * Export button below re-runs the export rather than handing back the bytes these
 * figures describe, which is why the timestamp and the note are shown.
 */
export const PreviewModal = ({
  open,
  exporting,
  dateFrom,
  dateTo,
  result,
  onExport,
  onClose,
}: PreviewModalProps) => {
  const { t, i18n } = useTranslation("ServiceTrackerExport");
  const { summary, plausibility, warnings, generatedAt, conflicts } = result;
  const locale = i18n.language;

  return (
    <Modal
      open={open}
      centered
      width={MODAL_WIDTH}
      title={t("modal.title", {
        from: formatDisplayDate(dateFrom),
        to: formatDisplayDate(dateTo),
      })}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          {t("modal.close")}
        </Button>,
        <Button
          key="export"
          type="primary"
          loading={exporting}
          disabled={summary.positions === 0}
          onClick={onExport}
        >
          {t("export")}
        </Button>,
      ]}
    >
      {/*
       * The backend's own conflict check (kontax-backend#926) is the
       * authority here, and it runs on preview too, so a conflict is visible
       * before the operator ever reaches the Export button rather than only
       * after — the local warning above the date fields is immediate
       * feedback while picking dates, this is what the backend actually
       * found against the full audit history.
       */}
      {conflicts && conflicts.length > 0 && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={t("conflict.title")}
          description={
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {conflicts.map((conflict) => (
                <li
                  key={`${conflict.kind}-${conflict.from}-${conflict.to}-${conflict.changedAt}`}
                >
                  {formatConflictMessage(conflict, t)}
                </li>
              ))}
            </ul>
          }
        />
      )}

      <Descriptions bordered column={2} size="small">
        <Descriptions.Item label={t("modal.positions")}>
          {summary.positions}
        </Descriptions.Item>
        <Descriptions.Item label={t("modal.invoices")}>
          {summary.invoices}
        </Descriptions.Item>
        <Descriptions.Item label={t("modal.customers")}>
          {summary.customers}
        </Descriptions.Item>
        <Descriptions.Item label={t("modal.grossTotal")}>
          {formatEuro(summary.grossTotalInEuro, locale)}
        </Descriptions.Item>
        <Descriptions.Item label={t("modal.skippedFreePositions")} span={2}>
          {summary.skippedFreePositions}
        </Descriptions.Item>
      </Descriptions>

      {plausibility && (
        <>
          <Typography.Title level={5} style={{ marginTop: 24 }}>
            {t("modal.plausibility")}
          </Typography.Title>

          <PositionTable
            title={t("modal.highest")}
            positions={plausibility.highest}
          />
          <PositionTable
            title={t("modal.lowest")}
            positions={plausibility.lowest}
          />

          {plausibility.unusualAmounts.length ? (
            <PositionTable
              title={t("modal.unusualAmounts")}
              positions={plausibility.unusualAmounts}
              withReason
            />
          ) : (
            <Alert
              style={{ marginTop: 8 }}
              type="success"
              message={t("modal.noUnusualAmounts")}
            />
          )}

          {/*
           * Reports a count the builder took, rather than printing a guarantee
           * nothing checked. It should always be green, and if it ever is not,
           * that is exactly the case worth seeing before an import.
           */}
          <Alert
            style={{ marginTop: 8 }}
            type={plausibility.positionsWithoutTitle ? "error" : "success"}
            message={
              plausibility.positionsWithoutTitle
                ? t("modal.titlesMissing", {
                    count: plausibility.positionsWithoutTitle,
                  })
                : t("modal.titlesComplete", { count: summary.positions })
            }
            description={`${t("modal.distinctTitles")}: ${
              plausibility.distinctTitles
            }`}
          />
        </>
      )}

      <Alert
        style={{ marginTop: 16 }}
        type={warnings.length ? "warning" : "success"}
        message={warnings.length ? t("modal.warnings") : t("modal.noWarnings")}
        description={
          warnings.length ? (
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          ) : undefined
        }
      />

      <Alert
        style={{ marginTop: 16 }}
        type="info"
        message={t("modal.generatedAt", {
          timestamp: new Date(generatedAt).toLocaleString(locale),
        })}
        description={t("modal.note")}
      />
    </Modal>
  );
};
